import { useState } from "react";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import Images from "../../../utils/Images";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import providerAuthService from "../../../api/ProviderAuthService";
import { useDispatch } from "react-redux";
import { setUser, setToken } from "../../../store/slices/authSlice";

export default function SignInEmail() {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [password, setPassword] = useState("");
  const dispatch = useDispatch();

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);    
    if (emailError) {
      setEmailError("");
    }
    if (value && !validateEmail(value)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handleContinue = async () => {
    setIsSubmitting(true);
    try {
      if (!email || !password) {
        toast.error("Please fill in all fields");
        return;
      }

      if (!validateEmail(email)) {
        setEmailError("Please enter a valid email address");
        toast.error("Please enter a valid email address");
        return;
      }

      const payload = { email_or_phone: email, password: password };
      const response = await providerAuthService.login(payload);
      
      if (response.status) {
        // Login successful
        const userData = response.data?.data?.data;
        toast.success(response.message);
        dispatch(setUser(userData));
        dispatch(setToken(userData?.token));
        
        // Check if user is verified and navigate accordingly
        if (userData?.email_verified_at || userData?.phone_number_verified_at) {
          // User is verified, redirect to dashboard
          navigate("/dashboard", { replace: true });
        } else {
          // User is not verified, redirect to verification page
          navigate("/verify-account", { replace: true });
        }
      } else {
        // Login failed
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.data?.reason || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      <img
        src="https://images.unsplash.com/photo-1517649763962-0c623066013b?q=80&w=1600&auto=format&fit=crop"
        alt="bg"
        className="object-cover absolute inset-0 w-full h-full"
      />
      <div className="absolute inset-0 bg-black/60" />
      <div className="flex relative z-10 justify-center items-start pt-10">
        <Link to="/">
        <img src={Images.logo_navbar} alt="TripSecure" className="h-10" />
        </Link>
      </div>
      <div className="flex relative z-10 justify-center items-center p-6">
        <div className="p-6 mt-4 w-full max-w-md rounded-3xl shadow-2xl bg-white/95">
          <h2 className="text-lg font-semibold text-center">
            Login to TripSecure
          </h2>
          <p className="mt-1 text-xs text-center text-gray-500">
            Please, enter your login information:
          </p>
          <div className="mt-5 space-y-4">
            {/* Email */}
            <div className="relative group">
              <div className={`relative rounded-xl p-[1px] transition-all duration-300 ${
                emailError 
                  ? "bg-gradient-to-br via-transparent from-red-400/30 to-red-400/30 group-hover:from-red-400/50 group-hover:to-red-400/50 group-focus-within:from-red-500/60 group-focus-within:to-red-500/60"
                  : "bg-gradient-to-br via-transparent from-emerald-400/30 to-emerald-400/30 group-hover:from-emerald-400/50 group-hover:to-emerald-400/50 group-focus-within:from-emerald-500/60 group-focus-within:to-emerald-500/60"
              }`}>
                <div className={`flex items-center gap-3 rounded-[0.95rem] bg-white/95 px-4 py-3 ring-1 shadow-sm backdrop-blur-md transition-all duration-300 ${
                  emailError
                    ? "ring-red-300/60 group-hover:ring-red-400/60 group-focus-within:ring-red-500/80 group-focus-within:shadow-lg group-focus-within:bg-white"
                    : "ring-gray-200/50 group-hover:ring-emerald-300/60 group-hover:shadow-md group-focus-within:ring-emerald-400/80 group-focus-within:shadow-lg group-focus-within:bg-white"
                }`}>
                  <Mail
                    size={18}
                    className={`transition-colors duration-300 ${
                      emailError 
                        ? "text-red-600 group-focus-within:text-red-700"
                        : "text-emerald-600 group-focus-within:text-emerald-700"
                    }`}
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="Email"
                    className="w-full text-sm font-medium bg-transparent border-0 transition-colors duration-300 outline-none focus:outline-none focus:ring-0 focus:border-0 focus:shadow-none placeholder:text-gray-400 placeholder:font-normal focus:text-gray-900"
                  />
                </div>
              </div>
              {emailError && (
                <div className="mt-1 text-xs text-red-600">
                  {emailError}
                </div>
              )}
            </div>

            {/* Password */}
            <div className="relative group">
              <div className="relative rounded-xl p-[1px] bg-gradient-to-br from-emerald-400/30 via-transparent to-emerald-400/30 transition-all duration-300 group-hover:from-emerald-400/50 group-hover:to-emerald-400/50 group-focus-within:from-emerald-500/60 group-focus-within:to-emerald-500/60">
                <div className="flex items-center gap-3 rounded-[0.95rem] bg-white/95 px-4 py-3 ring-1 ring-gray-200/50 shadow-sm backdrop-blur-md transition-all duration-300 group-hover:ring-emerald-300/60 group-hover:shadow-md group-focus-within:ring-emerald-400/80 group-focus-within:shadow-lg group-focus-within:bg-white">
                  <Lock
                    size={18}
                    className="text-emerald-600 transition-colors duration-300 group-focus-within:text-emerald-700"
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    className="w-full text-sm font-medium bg-transparent border-0 transition-colors duration-300 outline-none focus:outline-none focus:ring-0 focus:border-0 focus:shadow-none placeholder:text-gray-400 placeholder:font-normal focus:text-gray-900"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="text-gray-400 transition-colors duration-300 hover:text-emerald-600 focus:outline-none focus:text-emerald-700"
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              <Link
                to="/forgot-password"
                className="text-emerald-600 transition-colors hover:text-emerald-700"
              >
                Forgotten Password?
              </Link>
            </div>
            <button
              onClick={handleContinue}
              disabled={isSubmitting}
              className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:from-emerald-600 hover:to-emerald-700 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:ring-offset-2 disabled:opacity-80 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <span className="flex relative z-10 gap-2 justify-center items-center">
                {isSubmitting && (
                  <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent"></div>
                )}
                {isSubmitting ? "Logging in..." : "Login"}
              </span>
              <div className="absolute inset-0 bg-gradient-to-r transition-transform duration-700 -translate-x-full from-white/0 via-white/20 to-white/0 group-hover:translate-x-full"></div>
            </button>
            <p className="text-xs text-center text-gray-600">
              Not registered?{" "}
              <Link to="/signup" className="text-emerald-600">
                Register
              </Link>
            </p>
            <Link
              to="/signin"
              className="block text-xs text-center text-emerald-600"
            >
              Back to options
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
